import { ESdChangeReason, ICommonSettings } from "@kuika/kuika-cl-model";
import { ELDAPBindType, ELDAPProtocol } from "@kuika/kuika-cl-model/src/models/common-config";
import { Form } from "antd/es";
import { ReactNode } from "react";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../../shared/pure-sd";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { ConfigModalGroupHeading, ConfigModalNote } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";
import "./config-auth-provider.scss";

/** The fields inside this dialog, for the settings search. See `IConfigFieldProps.searchTerms`. */
export const APP_AUTH_LDAP_SEARCH_TERMS: string[] = [
  "Request",
  "Ip Address",
  "User Name",
  "Password",
  "Port",
  "Distinguished name",
  "Search Distinguished name",
  "Search Template",
  "Bind Type",
  "Protocol",
  "Domain Prefix For User Name",
  "User Name Attribute Name",
  "First Name Attribute Name",
  "Last Name Attribute Name",
  "Phone Number Attribute Name"
];

export interface IAppConfigAuthLdapState {}

export interface IAppConfigAuthLdapProps {
  editingId?: string;
  getSectionDescription: (contentKey: string) => ReactNode;
  commonSettings: ICommonSettings;
}

class AppConfigAuthLdap extends PureSdComponent<IAppConfigAuthLdapProps & IWithSDProps, IAppConfigAuthLdapState> {
  constructor(props: IAppConfigAuthLdapProps & IWithSDProps) {
    super(props);
    this.state = {};
  }

  existingNameValidator = async (_rule, value) => {
    const settings = this.props.commonSettings.ldapSettings;

    const isSettingAlreadyExist =
      settings?.some(
        (x) => x.name === value && x.id !== this.props.editingId && !x.isGlobalCommonConfigOnProjectConfig
      ) || value === "Kuika Auth";

    if (isSettingAlreadyExist) throw new Error("LDAP Settings Name is already exist.");
  };

  render() {
    return (
      <div className="kuika-auth-provider">
        <ConfigField
          label="Name"
          required
          tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_name"
        >
          <Form.Item
            name="ldapName"

            rules={[
              { required: true, message: "Please enter a LDAP Settings name" },
              { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Invalid LDAP Settings Name" },
              { validator: this.existingNameValidator }
            ]}
          >
            <KuikaInput size="md" />
          </Form.Item>
        </ConfigField>
        <div className="kuika-auth-provider__pane">
          <ConfigModalGroupHeading>Request</ConfigModalGroupHeading>
          <ConfigModalNote>
            {this.props.getSectionDescription(
              "authenticationprovidersettings_ldap_authenticationprovidersettings_request_description"
            )}
          </ConfigModalNote>
          <ConfigField
            label="Ip Address"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_ipadress"
          >
            <Form.Item name="ipAddress">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="User Name"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_username"
          >
            <Form.Item name="userName">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Password"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_password"
          >
            <Form.Item name="password">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Port"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_port"
          >
            <Form.Item name="port">
              <KuikaInput size="md" type="number" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Distinguished name"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_dn"
          >
            <Form.Item name="dn">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Search Distinguished name"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_searchdn"
          >
            <Form.Item name="searchDn">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Search Template"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_searchtemplate"
          >
            <Form.Item name="searchTemplate">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Bind Type"
            required
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_bindtype"
          >
            <Form.Item name="bindType" rules={[{ required: true, message: "Please select an LDAP Bind Type" }]}>
              <KuikaSelectbox
                size="md"
                options={[
                  { value: ELDAPBindType.Simple, label: "Simple" },
                  { value: ELDAPBindType.Anonymous, label: "Anonymous" },
                  { value: ELDAPBindType.Regular, label: "Regular" }
                ]}
              />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Protocol"
            required
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_protocol"
          >
            <Form.Item name="protocol" rules={[{ required: true, message: "Please select an LDAP Protocol" }]}>
              <KuikaSelectbox
                size="md"
                options={[
                  { value: ELDAPProtocol.LDAP, label: "LDAP" },
                  { value: ELDAPProtocol.LDAPS, label: "LDAPS" }
                ]}
              />
            </Form.Item>
          </ConfigField>
          <ConfigField label="Domain Prefix For User Name">
            <Form.Item name="domainPrefixForUserName">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="User Name Attribute Name"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_usernameattributename"
          >
            <Form.Item name="userNameAttributeName">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="First Name Attribute Name"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_firstnameattributename"
          >
            <Form.Item name="firstNameAttributeName">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Last Name Attribute Name"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_lastnameattributename"
          >
            <Form.Item name="lastNameAttributeName">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
          <ConfigField
            label="Phone Number Attribute Name"
            tooltipContentKey="authenticationprovidersettings_ldap_authenticationprovidersettings_request_phonenumberattributename"
          >
            <Form.Item name="phoneNumberAttributeName">
              <KuikaInput size="md" />
            </Form.Item>
          </ConfigField>
        </div>
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const appConfigAuthLdap = withSD<IAppConfigAuthLdapProps>(AppConfigAuthLdap);
export { appConfigAuthLdap as AppConfigAuthLdap };
